// src/components/ChangePasswordSection.js
//
// Self-contained "Change Password" row for the parent Account Hub
// (src/pages/AccountSettings.js). Mirrors the view-mode / edit-mode pattern of
// the Profile Information card, but owns ALL of its own state, validation, and
// error display — it reads nothing from AccountSettings and never touches the
// page-level error/success banners or the shared isSaving flag.
//
// Flow: reauthenticateWithCredential (current password) -> updatePassword.
// Frontend only; no Firestore writes, no Cloud Functions.
import React, { useState, useEffect } from 'react';
import { auth } from '../firebase';
import { EmailAuthProvider, reauthenticateWithCredential, updatePassword } from 'firebase/auth';
import { validatePassword } from '../utils/passwordValidation';
import { KeyRound, Pencil, Eye, EyeOff, CheckCircle } from 'lucide-react';

function ChangePasswordSection({ user }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isImpersonating, setIsImpersonating] = useState(false);

  // Mirror the impersonation detection in src/components/ImpersonationBanner.js
  // (custom claim key: impersonatedBy). During an admin impersonation session
  // there is no known password, so reauthentication cannot succeed — hide the
  // control rather than let it dead-end.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await user.getIdTokenResult();
        if (!cancelled) setIsImpersonating(Boolean(res.claims?.impersonatedBy));
      } catch (e) {
        if (!cancelled) setIsImpersonating(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  const resetFields = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
  };

  const handleStartEdit = () => {
    setError(null);
    setSuccess(null);
    setIsExpanded(true);
  };

  const handleCancel = () => {
    resetFields();
    setShowPasswords(false);
    setError(null);
    setIsExpanded(false);
  };

  const mapAuthError = (code) => {
    switch (code) {
      case 'auth/wrong-password':
      case 'auth/invalid-credential':
        return 'Current password is incorrect.';
      case 'auth/too-many-requests':
        return 'Too many attempts. Please wait a few minutes and try again.';
      case 'auth/weak-password':
        return 'That password is too weak. Please choose a stronger one.';
      case 'auth/requires-recent-login':
        return 'For security, please sign out and sign back in, then try again.';
      case 'auth/network-request-failed':
        return 'Network error. Please check your connection and try again.';
      default:
        return 'Could not update password. Please try again.';
    }
  };

  const handleUpdatePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('Please fill in all fields.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match.');
      return;
    }
    const rulesError = validatePassword(newPassword);
    if (rulesError) {
      setError(rulesError);
      return;
    }
    if (newPassword === currentPassword) {
      setError('New password must be different from your current password.');
      return;
    }

    try {
      setIsSaving(true);
      setError(null);
      setSuccess(null);

      const cred = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(auth.currentUser, cred);
      await updatePassword(auth.currentUser, newPassword);

      resetFields();
      setIsExpanded(false);
      setShowPasswords(false);
      setError(null);
      setSuccess('Password updated successfully.');
    } catch (err) {
      // Log the raw error for debugging — never any password value.
      console.error('ChangePasswordSection: password update failed', err);
      setError(mapAuthError(err?.code));
    } finally {
      setIsSaving(false);
    }
  };

  // ── Impersonation: collapsed row, no edit button ──────────────────────────
  if (isImpersonating) {
    return (
      <div className="p-4 bg-gray-50 rounded-xl">
        <div className="flex items-center gap-3">
          <KeyRound className="text-gray-500" size={20} />
          <div>
            <div className="font-semibold text-gray-800">••••••••</div>
            <div className="text-sm text-gray-600">
              Password changes are disabled during an admin impersonation session.
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Collapsed view (default) ─────────────────────────────────────────────
  if (!isExpanded) {
    return (
      <div>
        {success && (
          <div className="mb-2 text-sm font-medium text-green-600 flex items-center gap-2">
            <CheckCircle size={16} />
            {success}
          </div>
        )}
        <div className="p-4 bg-gray-50 rounded-xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <KeyRound className="text-gray-500" size={20} />
              <div>
                <div className="font-semibold text-gray-800">••••••••</div>
                <div className="text-sm text-gray-600">Password</div>
              </div>
            </div>
            <button
              onClick={handleStartEdit}
              className="p-2 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
              title="Change password"
            >
              <Pencil size={16} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Expanded view ───────────────────────────────────────────────────────
  return (
    <div className="p-4 bg-gray-50 rounded-xl">
      <div className="space-y-3">
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">
            Current Password
          </label>
          <input
            type={showPasswords ? 'text' : 'password'}
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            autoComplete="current-password"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm bg-white"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">
            New Password
          </label>
          <input
            type={showPasswords ? 'text' : 'password'}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            autoComplete="new-password"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm bg-white"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">
            Confirm New Password
          </label>
          <input
            type={showPasswords ? 'text' : 'password'}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            autoComplete="new-password"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm bg-white"
          />
        </div>

        <button
          type="button"
          onClick={() => setShowPasswords((v) => !v)}
          className="flex items-center gap-1.5 text-xs font-medium text-gray-500 hover:text-purple-600 transition-colors"
        >
          {showPasswords ? <EyeOff size={14} /> : <Eye size={14} />}
          {showPasswords ? 'Hide passwords' : 'Show passwords'}
        </button>

        <div className="text-xs text-gray-500">
          Changing your password will sign you out on your other devices.
        </div>

        {error && (
          <div className="text-sm font-medium text-red-600">{error}</div>
        )}

        <div className="flex gap-2">
          <button
            onClick={handleUpdatePassword}
            disabled={isSaving}
            className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {isSaving ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <CheckCircle size={16} />
            )}
            Update Password
          </button>
          <button
            onClick={handleCancel}
            disabled={isSaving}
            className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 text-sm font-semibold rounded-lg transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export default ChangePasswordSection;
